/**
 * iter163h — Dark, mobile-friendly DM modal.
 *
 * Two-pane layout:
 *   - Left: chat list (people you've messaged) + "new chat" picker
 *   - Right: active conversation thread + composer
 *
 * On mobile the right pane covers the left when a conversation is open;
 * a back button returns to the list.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { X, ArrowLeft, Search, Plus, Send, Check, CheckCheck, CornerUpLeft, ShieldCheck } from 'lucide-react';
import { dmApi } from '../../lib/api';
import { useDM } from '../../contexts/DMContext';
import { useAuth } from '../../contexts/AuthContext';
import DMRichCard from './DMRichCard';
import { toast } from 'sonner';

const REACTIONS = ['👍', '❤️', '✅'];

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

export default function DMModal() {
  const { user } = useAuth();
  const { setOpen, refreshUnread, activeConvId, setActiveConvId, subscribe, getDraft, setDraft, pendingDM, setPendingDM } = useDM();
  const [convos, setConvos] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [activePeer, setActivePeer] = useState(null);
  const [messages, setMessages] = useState([]);
  // iter163i — composer text is read from DMContext per-conv draft so it
  // survives outside-click close → reopen.
  const text = activeConvId ? getDraft(activeConvId) : '';
  const setText = (val) => activeConvId && setDraft(activeConvId, typeof val === 'function' ? val(text) : val);
  const [searchInChat, setSearchInChat] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  const [statusInput, setStatusInput] = useState('');
  const [statusEditing, setStatusEditing] = useState(false);
  const [typingPeer, setTypingPeer] = useState(false);
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashKind, setSlashKind] = useState(null); // 'req' | 'doc'
  const [slashItems, setSlashItems] = useState([]);
  const [embed, setEmbed] = useState(null); // { kind, ref_no?, doc_id?, label }
  const listEndRef = useRef(null);
  const typingTimerRef = useRef(null);
  const inputRef = useRef(null);
  const sheetRef = useRef(null);

  // ---------- Loaders ----------
  const loadConvos = useCallback(async () => {
    try {
      const r = await dmApi.listConversations();
      setConvos(r.data || []);
    } catch (_) { /* */ }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await dmApi.getStatus();
      setStatusInput(r.data?.message || '');
    } catch (_) { /* */ }
  }, []);

  const loadMessages = useCallback(async (cid, search = '') => {
    try {
      const r = await dmApi.listMessages(cid, search ? { search, limit: 100 } : { limit: 50 });
      setMessages(r.data?.items || []);
      setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' }), 50);
      refreshUnread();
    } catch (_) { /* */ }
  }, [refreshUnread]);

  useEffect(() => { loadConvos(); loadStatus(); }, [loadConvos, loadStatus]);

  // ---------- Open conversation ----------
  const openConv = useCallback(async (peer) => {
    setActivePeer(peer);
    try {
      const r = await dmApi.startConversation(peer.id);
      setActiveConvId(r.data.id);
      await loadMessages(r.data.id);
      loadConvos();
    } catch (_) { /* */ }
  }, [loadConvos, loadMessages, setActiveConvId]);

  const openConvById = useCallback(async (convo) => {
    setActivePeer(convo.peer);
    setActiveConvId(convo.id);
    await loadMessages(convo.id);
    loadConvos();
  }, [loadConvos, loadMessages, setActiveConvId]);

  // ---------- WS push handlers ----------
  useEffect(() => {
    const unsub = subscribe((data) => {
      const t = data.type;
      if (t === 'dm:message:new') {
        const m = data.message;
        // If it's for the open chat, append and scroll
        if (activeConvId && m.conversation_id === activeConvId) {
          // iter163j — De-dup against the optimistic temp message we
          // appended on send. If the server message matches a recent
          // optimistic from us, swap it in place.
          setMessages((prev) => {
            if (m.sender_id === user?.id) {
              const tempIdx = prev.findIndex(
                (x) => x._optimistic && x.text === m.text && Math.abs(new Date(x.created_at) - new Date(m.created_at)) < 30000
              );
              if (tempIdx !== -1) {
                const copy = [...prev];
                copy[tempIdx] = m;
                return copy;
              }
            }
            // Skip if we already have this id (multi-tab safety).
            if (prev.some((x) => x.id === m.id)) return prev;
            return [...prev, m];
          });
          setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
          // Auto-mark read if it came from the peer
          if (m.sender_id !== user?.id) {
            dmApi.markRead(m.id).catch(() => {});
            refreshUnread();
          }
        }
        loadConvos();
      } else if (t === 'dm:message:reaction') {
        setMessages((prev) => prev.map((mm) => (mm.id === data.message_id ? { ...mm, reactions: data.reactions } : mm)));
      } else if (t === 'dm:message:read') {
        setMessages((prev) => prev.map((mm) => (mm.id === data.message_id ? { ...mm, read_by_recipient: true } : mm)));
      } else if (t === 'dm:typing') {
        if (activeConvId && data.conversation_id === activeConvId && data.user_id !== user?.id) {
          setTypingPeer(true);
          if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
          typingTimerRef.current = setTimeout(() => setTypingPeer(false), 3500);
        }
      } else if (t === 'dm:status') {
        // Refresh convo list so peer status updates in sidebar
        loadConvos();
      }
    });
    return unsub;
  }, [subscribe, activeConvId, user?.id, loadConvos, refreshUnread]);

  // ---------- New chat user picker ----------
  useEffect(() => {
    if (!pickerOpen) return;
    dmApi.listUsers().then((r) => setUsers(r.data || []));
  }, [pickerOpen]);

  // ---------- Slash autocomplete ----------
  useEffect(() => {
    // iter163m — Typing just "/" (or "/ <prefix>") with no command yet
    // shows a help menu listing the available slash commands.
    const helpMatch = text.match(/(^|\s)\/([a-z]*)$/i);
    if (helpMatch && !['req', 'doc'].includes(helpMatch[2].toLowerCase())) {
      setSlashKind('help');
      setSlashOpen(true);
      setSlashItems([
        { id: '__help_req', _help: true, command: '/req', hint: 'attach a request card (ref no or title)' },
        { id: '__help_doc', _help: true, command: '/doc', hint: 'attach a document card (filename)' },
      ]);
      return;
    }
    const m = text.match(/(^|\s)\/(req|doc)(?:\s+(.*))?$/i);
    if (!m) {
      setSlashOpen(false);
      return;
    }
    const kind = m[2].toLowerCase();
    const q = m[3] || '';
    setSlashKind(kind);
    setSlashOpen(true);
    const fn = kind === 'req' ? dmApi.slashReq : dmApi.slashDoc;
    fn(q).then((r) => setSlashItems(r.data?.items || [])).catch(() => setSlashItems([]));
  }, [text]);

  const pickSlash = (item) => {
    if (item._help) {
      // Replace the trailing "/" with the chosen command + a trailing space,
      // letting the user keep typing the query.
      setText((prev) => prev.replace(/(^|\s)\/([a-z]*)$/i, (m0, lead) => `${lead}${item.command} `));
      setSlashOpen(false);
      inputRef.current?.focus();
      return;
    }
    if (slashKind === 'req') {
      setEmbed({
        kind: 'request', ref_no: item.ref_no,
        label: `${item.ref_no} — ${item.title || ''}`,
      });
    } else {
      setEmbed({
        kind: 'document', doc_id: item.id,
        label: item.filename,
      });
    }
    // Strip the slash token from text
    setText((prev) => prev.replace(/(^|\s)\/(req|doc)(\s+.*)?$/i, '$1').trimEnd());
    setSlashOpen(false);
    inputRef.current?.focus();
  };

  // ---------- Send ----------
  const send = async (e) => {
    e?.preventDefault();
    const value = (text || '').trim();
    if (!value && !embed) return;
    if (!activeConvId) return;
    const payload = {
      text: value || (embed ? embed.label : ''),
      embed_kind: embed?.kind || null,
      embed_ref_no: embed?.ref_no || null,
      embed_doc_id: embed?.doc_id || null,
    };
    // iter163j — Optimistic send: append a temp message immediately and
    // clear the draft so the UI feels instant. The real server message
    // arrives over WebSocket (dm:message:new) and is appended on top —
    // we de-dup by remembering the temp id and reconciling on server reply.
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      conversation_id: activeConvId,
      sender_id: user?.id,
      sender_name: user?.name,
      text: payload.text,
      embed_kind: payload.embed_kind,
      embed_ref_no: payload.embed_ref_no,
      embed_doc_id: payload.embed_doc_id,
      embed_snapshot: embed ? { ref_no: embed.ref_no, title: embed.label, status: '', type: 'CTA' } : null,
      created_at: new Date().toISOString(),
      read_by_recipient: false,
      reactions: {},
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft(activeConvId, '');
    setEmbed(null);
    setSlashOpen(false);
    setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 30);
    try {
      const r = await dmApi.sendMessage(activeConvId, payload);
      // Reconcile: replace optimistic with the real message.
      setMessages((prev) => prev.map((mm) => (mm.id === tempId ? r.data : mm)));
    } catch (err) {
      // Roll back optimistic on failure + restore draft.
      setMessages((prev) => prev.filter((mm) => mm.id !== tempId));
      setDraft(activeConvId, payload.text);
      toast.error('Could not send message');
    }
  };

  // ---------- Typing indicator ----------
  const handleType = (e) => {
    if (activeConvId) setDraft(activeConvId, e.target.value);
    if (activeConvId) dmApi.typing(activeConvId).catch(() => {});
  };

  // ---------- Status save ----------
  const saveStatus = async () => {
    try {
      await dmApi.setStatus(statusInput);
      setStatusEditing(false);
      loadConvos();
      toast.success('Status updated');
    } catch (_) { toast.error('Could not save status'); }
  };

  // ---------- Reaction ----------
  const toggleReaction = async (mid, emoji) => {
    try {
      await dmApi.toggleReaction(mid, emoji);
    } catch (_) { /* */ }
  };

  // ---------- In-chat search ----------
  const runSearch = () => {
    if (!activeConvId) return;
    loadMessages(activeConvId, searchInChat);
  };

  // ---------- "Discuss in DM" deep-link ----------
  // When the caller (e.g. RequestDetails "Discuss in DM" button) sets a
  // pendingDM payload on DMContext, we auto-open that conversation and
  // pre-attach the request card.
  useEffect(() => {
    if (!pendingDM) return;
    (async () => {
      try {
        const r = await dmApi.startConversation(pendingDM.user_id);
        setActivePeer(pendingDM.peer || { id: pendingDM.user_id, name: pendingDM.peer_name || 'User', role: '' });
        setActiveConvId(r.data.id);
        await loadMessages(r.data.id);
        loadConvos();
        if (pendingDM.embed) {
          setEmbed(pendingDM.embed);
          setTimeout(() => inputRef.current?.focus(), 100);
        }
      } catch (_) { /* */ }
      setPendingDM(null);
    })();
  }, [pendingDM, loadConvos, loadMessages, setActiveConvId, setPendingDM]);

  // ---------- Escape key closes modal (drafts persist) ----------
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        // iter163k — also reset activeConvId so the reopened list shows
        // the "Draft: …" preview for the conv that was being composed.
        setActivePeer(null);
        setActiveConvId(null);
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen, setActiveConvId]);

  // ---------- Filtered users for picker ----------
  // iter166e — Broadened the New-chat picker filter to match name + email +
  // role, mirroring the @-mention recipient picker. The original filter was
  // name-only, which made it hard to find an "investigator" by typing the
  // role.
  const filteredUsers = users.filter((u) => {
    if (!pickerQuery) return true;
    const q = pickerQuery.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  });

  // ---------- Outside-click backdrop handler ----------
  // Drafts are preserved in DMContext, so closing here loses nothing.
  const handleBackdropMouseDown = (e) => {
    if (sheetRef.current && !sheetRef.current.contains(e.target)) {
      // iter163k — Reset the active conversation when the modal closes so
      // that on reopen the chat list shows the WhatsApp-style "Draft: …"
      // preview for the conv the user was just composing in. Drafts
      // themselves persist in DMContext.
      setActivePeer(null);
      setActiveConvId(null);
      setOpen(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-950/70 backdrop-blur-sm sm:items-center sm:p-4 lg:p-6"
      onMouseDown={handleBackdropMouseDown}
      data-testid="dm-modal"
    >
      <div
        ref={sheetRef}
        className="flex h-full max-h-[100dvh] w-full max-w-5xl flex-col overflow-hidden bg-slate-900 text-slate-100 shadow-2xl ring-1 ring-white/5 sm:h-[calc(100dvh-3rem)] sm:rounded-2xl lg:h-[calc(100dvh-3rem)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
          <div className="flex items-center gap-2">
            {activePeer && (
              <button
                onClick={() => { setActivePeer(null); setActiveConvId(null); setMessages([]); }}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-white sm:hidden"
                data-testid="dm-back-btn"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-sm font-semibold tracking-wide">Private Messages</h2>
            <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase text-slate-400">
              Encrypted view
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              // iter163k — Reset active conv on close so the chat list
              // shows the "Draft: …" preview when the modal is reopened.
              setActivePeer(null);
              setActiveConvId(null);
              setOpen(false);
            }}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Close messages"
            data-testid="dm-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* iter163l — Privacy banner. Redesigned with a soft gradient
            background + emerald shield icon to convey "your conversations
            are safe here" without being preachy. Copy tightened to two
            short, scannable lines. Non-dismissible by design. */}
        <div
          className="flex items-start gap-3 border-b border-slate-800 bg-gradient-to-r from-slate-950 via-emerald-950/30 to-slate-950 px-4 py-3"
          data-testid="dm-privacy-banner"
        >
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/40">
            <ShieldCheck size={14} className="text-emerald-300" />
          </div>
          <div className="min-w-0 flex-1 leading-relaxed">
            <p className="text-[12.5px] font-semibold text-slate-100">
              Just between you two. Always.
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">
              No one else — not admins, not IT, not even email — can read what&apos;s here.
            </p>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Left pane */}
          <aside
            className={`flex w-full sm:w-80 flex-col border-r border-slate-800 ${
              activePeer ? 'hidden sm:flex' : 'flex'
            }`}
          >
            {/* My status */}
            <div className="border-b border-slate-800 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">My status</div>
              {statusEditing ? (
                <div className="mt-1 flex items-center gap-2">
                  <input
                    value={statusInput}
                    onChange={(e) => setStatusInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveStatus()}
                    placeholder="On vacation till Friday"
                    maxLength={120}
                    className="flex-1 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-slate-500 focus:outline-none"
                    data-testid="dm-status-input"
                  />
                  <button onClick={saveStatus} className="rounded-md bg-slate-700 px-2 py-1 text-xs hover:bg-slate-600" data-testid="dm-status-save">Save</button>
                </div>
              ) : (
                <button
                  onClick={() => setStatusEditing(true)}
                  className="mt-1 w-full truncate text-left text-sm text-slate-300 hover:text-white"
                  data-testid="dm-status-display"
                >
                  {statusInput || <span className="italic text-slate-500">Set a status…</span>}
                </button>
              )}
            </div>

            {/* New chat */}
            <div className="px-4 py-3">
              <button
                onClick={() => setPickerOpen((p) => !p)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm hover:bg-slate-700"
                data-testid="dm-new-chat-btn"
              >
                <Plus size={14} /> New chat
              </button>
              {pickerOpen && (
                <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-slate-800 bg-slate-900/80">
                  <div className="sticky top-0 border-b border-slate-800 bg-slate-900 px-2 py-1.5">
                    <input
                      placeholder="Search people…"
                      value={pickerQuery}
                      onChange={(e) => setPickerQuery(e.target.value)}
                      className="w-full rounded-md bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:outline-none"
                      data-testid="dm-picker-search"
                    />
                  </div>
                  {filteredUsers.length === 0 && (
                    <div className="px-3 py-4 text-center text-xs text-slate-500">No users</div>
                  )}
                  {filteredUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => { setPickerOpen(false); openConv(u); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-800"
                      data-testid={`dm-picker-user-${u.id}`}
                    >
                      <PresenceDot online={u.is_online} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{u.name}</div>
                        <div className="truncate text-[11px] text-slate-500">
                          {u.role}
                          {u.status_message ? ` · ${u.status_message}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Conversation list */}
            <div className="flex-1 overflow-y-auto" data-testid="dm-conv-list">
              {convos.length === 0 && (
                <div className="px-4 py-10 text-center text-xs text-slate-500">
                  Start a new chat to break the email cycle.
                </div>
              )}
              {convos.map((c) => {
                // iter163j — WhatsApp-style draft preview: if there's a
                // half-typed message for this conv, show "Draft: …" in cyan
                // instead of the last message preview.
                const draftText = (getDraft(c.id) || '').trim();
                const showDraft = draftText.length > 0 && c.id !== activeConvId;
                return (
                <button
                  key={c.id}
                  onClick={() => openConvById(c)}
                  className={`flex w-full items-start gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-800/60 ${
                    activeConvId === c.id ? 'bg-slate-800/80' : ''
                  }`}
                  data-testid={`dm-conv-${c.id}`}
                >
                  <PresenceDot online={c.peer?.is_online} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="truncate text-sm font-semibold text-slate-100">{c.peer?.name}</div>
                      <div className="text-[10px] text-slate-500">{timeAgo(c.last_message_at)}</div>
                    </div>
                    <div className="truncate text-[11px] text-slate-500">
                      {c.peer?.status_message ? <span className="italic">{c.peer.status_message}</span> : c.peer?.role}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="truncate text-xs text-slate-400">
                        {showDraft ? (
                          <span data-testid={`dm-conv-draft-${c.id}`}>
                            <span className="font-semibold text-cyan-400">Draft:</span>{' '}
                            <span className="text-slate-300">{draftText}</span>
                          </span>
                        ) : (
                          c.last_message_preview || ''
                        )}
                      </div>
                      {c.unread_count > 0 && (
                        <span className="ml-auto rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                          {c.unread_count > 9 ? '9+' : c.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
              })}
            </div>
          </aside>

          {/* Right pane */}
          <section className={`flex min-w-0 flex-1 flex-col ${activePeer ? 'flex' : 'hidden sm:flex'}`} data-testid="dm-thread-pane">
            {activePeer ? (
              <>
                {/* Peer header */}
                <header className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <PresenceDot online={activePeer.is_online} />
                    <div>
                      <div className="text-sm font-semibold">{activePeer.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {activePeer.role}
                        {activePeer.is_online ? ' · online' : ''}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSearchMode((s) => !s)}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
                    title="Search in this chat"
                    data-testid="dm-search-toggle"
                  >
                    <Search size={16} />
                  </button>
                </header>
                {searchMode && (
                  <div className="border-b border-slate-800 bg-slate-900 px-4 py-2">
                    <input
                      placeholder="Search in this chat…"
                      value={searchInChat}
                      onChange={(e) => setSearchInChat(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                      className="w-full rounded-md bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:outline-none"
                      data-testid="dm-search-input"
                    />
                  </div>
                )}

                {/* Messages — iter163j: improved typography, larger bubbles,
                    consecutive-message grouping, and a subtle pop-in animation. */}
                <div className="flex-1 overflow-y-auto px-4 py-5" data-testid="dm-messages">
                  {messages.length === 0 && (
                    <div className="py-20 text-center text-sm text-slate-500">
                      No messages yet. Say hi 👋
                    </div>
                  )}
                  {messages.map((m, idx) => {
                    const mine = m.sender_id === user?.id;
                    const prev = messages[idx - 1];
                    const isGrouped = prev && prev.sender_id === m.sender_id
                      && (new Date(m.created_at) - new Date(prev.created_at)) < 5 * 60 * 1000;
                    return (
                      <div
                        key={m.id}
                        className={`group flex items-end gap-2 ${isGrouped ? 'mt-1' : 'mt-4'} ${mine ? 'justify-end' : 'justify-start'} dm-msg-enter`}
                        data-testid={`dm-msg-${m.id}`}
                      >
                        {/* Peer avatar (left side, on first message of a group) */}
                        {!mine && !isGrouped && (
                          <div className="flex h-7 w-7 shrink-0 select-none items-center justify-center rounded-full bg-gradient-to-br from-slate-700 to-slate-800 text-[10px] font-semibold text-slate-200 ring-1 ring-slate-700">
                            {(m.sender_name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                        )}
                        {!mine && isGrouped && <div className="w-7 shrink-0" />}

                        <div
                          className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed shadow-sm ${
                            mine
                              ? 'rounded-br-md bg-gradient-to-br from-blue-600 to-blue-700 text-white'
                              : 'rounded-bl-md bg-slate-800 text-slate-100 ring-1 ring-slate-700/60'
                          }`}
                        >
                          <div className="whitespace-pre-wrap break-words">{m.text}</div>
                          {m.embed_kind && (
                            <DMRichCard kind={m.embed_kind} snapshot={m.embed_snapshot} />
                          )}
                          <div className={`mt-1 flex items-center justify-end gap-1.5 text-[10px] ${mine ? 'text-blue-200/80' : 'text-slate-500'}`}>
                            <span>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            {mine && (
                              m.read_by_recipient ? <CheckCheck size={12} className="text-emerald-300" /> : <Check size={12} />
                            )}
                          </div>
                          {/* Reactions row */}
                          {(Object.keys(m.reactions || {}).length > 0) && (
                            <div className="mt-1.5 flex items-center gap-1.5">
                              {REACTIONS.map((emoji) => {
                                const list = (m.reactions || {})[emoji] || [];
                                if (list.length === 0) return null;
                                const i_reacted = list.includes(user?.id);
                                return (
                                  <button
                                    key={emoji}
                                    onClick={() => toggleReaction(m.id, emoji)}
                                    className={`rounded-full px-2 py-0.5 text-[11px] transition-colors ${
                                      i_reacted ? 'bg-slate-700 ring-1 ring-blue-300' : 'bg-slate-700/60 hover:bg-slate-700'
                                    }`}
                                    data-testid={`dm-reaction-${m.id}-${emoji}`}
                                  >
                                    {emoji} {list.length}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Hover-only quick reactions */}
                        <div className={`flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 ${mine ? 'order-first' : ''}`}>
                          {REACTIONS.map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => toggleReaction(m.id, emoji)}
                              className="rounded-full bg-slate-800 px-1.5 py-0.5 text-xs ring-1 ring-slate-700 hover:bg-slate-700"
                              data-testid={`dm-add-reaction-${m.id}-${emoji}`}
                              title={`React with ${emoji}`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {typingPeer && (
                    <div className="text-xs italic text-slate-500" data-testid="dm-typing-indicator">
                      {activePeer.name} is typing…
                    </div>
                  )}
                  <div ref={listEndRef} />
                </div>

                {/* Composer */}
                <form onSubmit={send} className="relative border-t border-slate-800 bg-slate-900/90 px-4 py-3" data-testid="dm-composer">
                  {embed && (
                    <div className="mb-2 flex items-center gap-2 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs">
                      <CornerUpLeft size={12} className="text-slate-400" />
                      <span className="flex-1 truncate text-slate-300">{embed.label}</span>
                      <button type="button" onClick={() => setEmbed(null)} className="text-slate-500 hover:text-white">
                        <X size={12} />
                      </button>
                    </div>
                  )}
                  {slashOpen && (
                    <div className="absolute bottom-full left-4 right-4 mb-2 max-h-60 overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 shadow-xl ring-1 ring-white/5" data-testid="dm-slash-picker">
                      {slashKind === 'help' && (
                        <div className="border-b border-slate-800 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                          Slash commands
                        </div>
                      )}
                      {slashItems.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-500">No matches</div>
                      )}
                      {slashItems.map((it) => (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => pickSlash(it)}
                          className="block w-full px-3 py-2 text-left text-xs hover:bg-slate-800"
                          data-testid={`dm-slash-item-${it.id}`}
                        >
                          {it._help ? (
                            <div className="flex items-center gap-2">
                              <span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-emerald-300">{it.command}</span>
                              <span className="text-slate-400">{it.hint}</span>
                            </div>
                          ) : slashKind === 'req' ? (
                            <>
                              <span className="font-mono text-slate-300">{it.ref_no}</span>{' '}
                              <span className="text-slate-500">— {it.title}</span>
                            </>
                          ) : (
                            <span className="text-slate-300">{it.filename}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={inputRef}
                      value={text}
                      onChange={handleType}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          send();
                        }
                      }}
                      rows={1}
                      placeholder="Message…  (try /req or /doc)"
                      className="flex-1 resize-none rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-slate-500 focus:outline-none"
                      data-testid="dm-input"
                    />
                    <button
                      type="submit"
                      className="group inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-slate-100 ring-1 ring-slate-700 transition-colors hover:bg-blue-600 hover:text-white hover:ring-blue-500 active:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-800/60 disabled:text-slate-600 disabled:hover:bg-slate-800/60 disabled:hover:text-slate-600 disabled:hover:ring-slate-700"
                      disabled={!(text || '').trim() && !embed}
                      data-testid="dm-send-btn"
                      aria-label="Send message"
                    >
                      <Send size={16} className="transition-transform group-hover:translate-x-[1px]" />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-500" data-testid="dm-empty-state">
                Pick a conversation or start a new one.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function PresenceDot({ online }) {
  return (
    <span
      className={`mt-1.5 inline-block h-2.5 w-2.5 rounded-full ${
        online ? 'bg-emerald-400 ring-2 ring-emerald-400/30' : 'bg-slate-600'
      }`}
      aria-label={online ? 'online' : 'offline'}
      data-testid={`dm-presence-${online ? 'on' : 'off'}`}
    />
  );
}
